export const NODE_WIDTH = 180;
export const NODE_HEIGHT = 30;

// Graphic mode's artwork sizing.
//
// ONE SCALE FOR EVERY GRAPHIC. The artwork is authored on a common canvas in PowerPoint — the
// rack-width kit is drawn ~300px wide whatever it is, and height carries the rack units: a 1U
// switch 76, a 2U server 106, a switch chassis 268. All the relative sizing is already in the
// files, so the canvas multiplies by one number and gets the diagram that was drawn.
//
// It used to normalise on HEIGHT, and that is precisely what destroys the author's judgement.
// The core switch is the widest artwork in the library at 324px and normalising rendered it the
// NARROWEST node at 77px, because it is also the tallest. A 1U switch and a 4U chassis came out
// the same height by construction, which is the one distinction the artwork exists to make.
//
// Height normalisation was right while the files were cropped at arbitrary resolutions — a
// router 182px wide next to a server at 1154px — because there was no shared scale to honour.
// There is now. If a future graphic arrives off-scale, it will look wrong on the canvas rather
// than quietly rescaling everything around it, which is the better failure.
export const SHAPE_SCALE = 0.4;
// Only for a graphic with no recorded dimensions — uploaded before they were captured, or a file
// the browser could not decode.
const SHAPE_FALLBACK = 64;
// Guards against a pathological file, not a shaping rule: at 0.4 the largest real graphic is
// 130x107 and the smallest 46x46, both comfortably inside these.
const SHAPE_MAX = 220;
const SHAPE_MIN = 20;
// A graphic narrower than its caption still has to leave room for the caption.
export const SHAPE_LABEL_MIN_W = 80;
// How far the planning canvas zooms in (CoreCanvas). Shared with the graphics library, which
// judges whether a file has the pixels to stay sharp this far in (features/shapes/lib/
// graphic-resolution.ts): two copies of the number drift, and the symptom is a graphic passed as
// sharp that blurs on the canvas.
export const CANVAS_MAX_ZOOM = 4;

/**
 * Does this node draw as an ICON? In Icons mode everything does. In Graphic mode a SERVICE does
 * too: graphics are equipment artwork, and a service is a container of other objects, not a piece
 * of equipment (Andrew, 2026-09-11 — "for services you shouldn't be using a graphic, we should be
 * using the icon"). Same service test as CustomNodes' isServiceNode.
 *
 * SHARED by CustomNodes (what it draws) and elk.js (the footprint it reserves), like shapeBox:
 * two copies drift, and the symptom is overlapping nodes that read as an ELK bug.
 */
export function drawsAsIcon(nodeMode, data, type) {
  if (nodeMode === "Icons") return true;
  return nodeMode === "Graphic" && (data?.objectservice === "Service" || type === "service");
}

/**
 * The box a graphic draws in: its own dimensions at the shared scale.
 *
 * SHARED BY THE RENDERER AND ELK ON PURPOSE. CustomNodes draws this box and elk.js reserves room
 * for it; two copies of the arithmetic drift, and the symptom is not an obviously wrong size but
 * nodes overlapping or gaps the layout cannot explain, which reads as an ELK bug.
 *
 * The clamps adjust the SCALE, never one axis, so the aspect ratio is preserved whatever happens.
 * Clamping width and height independently would stretch the artwork, and a squashed switch is the
 * one thing people notice instantly without being able to say why.
 */
export function shapeBox(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!w || !h) return { w: SHAPE_FALLBACK, h: SHAPE_FALLBACK };
  let scale = SHAPE_SCALE;
  if (Math.min(w, h) * scale < SHAPE_MIN) scale = SHAPE_MIN / Math.min(w, h);
  // Applied last so nothing can exceed the cap, even after the minimum pushed the scale up.
  if (Math.max(w, h) * scale > SHAPE_MAX) scale = SHAPE_MAX / Math.max(w, h);
  return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

export const EDGE_COLOR = { normal: "#999", highlighted: "#0077ff" };
export const EDGE_THICKNESS = { normal: 1.5, highlighted: 3 };

/**
 * The BORDER a node gets in Status colour mode (Andrew, 14 Oct 2026: "change the red and green to
 * a bolder red, amber and green, and make red pulse"). Stronger than statusToColor, which stays
 * the pale fill used behind text and in the details panel.
 */
export const statusToBorderColor = (status) => {
  switch (status) {
    case 4: return "#16A34A";            // Green - no confirmed detections
    case 5:
    case 3: return "#F59E0B";            // Amber - suspicious / information
    case 2: return "#DC2626";            // Red - compromised (and it pulses: CustomNodes.css)
    case 1: return "#3B82F6";            // Blue - in progress
    case 0: return "#9CA3AF";            // Grey - not run
    case 10: return "#EAB308";           // Yellow - failed
    default: return null;
  }
};

export const statusToColor = (status) => {
  switch (status) {
    case 4:
      return "#B4E197";  // Green — no confirmed detections
    // 5 is AMBER, not green. It means attacker-associated evidence, uncorroborated — configuration hardening moved to its own posture class (scanner df8a055) and never escalates, so what is left in 5 must not read as clean. analytics-status.tsx is the canonical mapping; this copy was left behind when 5 was re-coloured.
    case 5:
    case 3:
      return "#FFD699";  // Amber — suspicious / information
    case 2:
      return "#FFB3B3";  // Red — compromised
    case 1:
      return "#93C5FD";  // Blue — in progress
    case 0:
      return "#CCCCCC";  // Grey — not run
    case 10:
      return "#FCD34D";  // Yellow — failed
    default:
      return "#E0E0E0";  // Default grey
  }
};
